import { useRef, useState } from "react";
import ApproveDenyModal from "../../components/RootElements/ApproveDenyModal";
import { noteStatusLabel, noteStatusStyle } from "./constants";
import SearchInput from "./SearchInput";

const fmt = (d) => d ? new Date(d).toLocaleString("sv-SE") : "–";
const fmtDate = (d) => d ? new Date(d).toLocaleDateString("sv-SE") : "–";

const emptyDraft = { note: "", type: "", date: "", status: "planned" };

const sortTorks = (torks) =>
    torks.slice().sort((a, b) => {
        const aNum = Number(a.name);
        const bNum = Number(b.name);
        const aIsNum = !isNaN(aNum) && a.name.trim() !== "";
        const bIsNum = !isNaN(bNum) && b.name.trim() !== "";
        if (aIsNum && bIsNum) return aNum - bNum;
        if (aIsNum) return -1;
        if (bIsNum) return 1;
        return a.name.localeCompare(b.name, "sv");
    });

/* Manage kilns as a responsive grid of glanceable tiles.
   Each tile leads with the kiln name + a status badge; active notes are
   compact rows and the add-note form is collapsed behind a button. */
const TorksManager = ({ torks, noteTypes, onCreate, onUpdate, onDelete, onAddNote, onUpdateNote, onNoteStatus, onDeleteNote, readOnly = false }) => {
    const [showForm, setShowForm] = useState(false);
    const [editing, setEditing] = useState(null);
    const [name, setName] = useState("");
    const [description, setDescription] = useState("");
    const [historyOpen, setHistoryOpen] = useState({});
    const [noteFormOpen, setNoteFormOpen] = useState({});
    const [noteDrafts, setNoteDrafts] = useState({});
    const [noteEdits, setNoteEdits] = useState({}); // inline edits for existing notes
    const [confirmDeleteTorkId, setConfirmDeleteTorkId] = useState(null);
    const [confirmDeleteNoteKey, setConfirmDeleteNoteKey] = useState(null);
    const [onlyWithNotes, setOnlyWithNotes] = useState(false);
    const [search, setSearch] = useState("");

    const getDraft = (torkId) => noteDrafts[torkId] || emptyDraft;
    const patchDraft = (torkId, patch) =>
        setNoteDrafts(prev => ({ ...prev, [torkId]: { ...getDraft(torkId), ...patch } }));

    // Inline editing helpers for existing notes
    const getNoteEdit = (torkId, noteId, note) => {
        const key = `${torkId}:${noteId}`;
        const defaults = {
            note: note.note || "",
            date: note.date ? new Date(note.date).toISOString().substring(0, 10) : "",
        };
        // Merge so untouched fields always fall back to the note's current values
        return noteEdits[key] ? { ...defaults, ...noteEdits[key] } : defaults;
    };
    const patchNoteEdit = (torkId, noteId, patch) => {
        const key = `${torkId}:${noteId}`;
        setNoteEdits(prev => ({ ...prev, [key]: { ...prev[key], ...patch } }));
    };
    const saveNoteIfChanged = (torkId, n) => {
        const key = `${torkId}:${n.id}`;
        if (!noteEdits[key]) return;
        const local = getNoteEdit(torkId, n.id, n);
        const origNote = n.note || "";
        const origDate = n.date ? new Date(n.date).toISOString().substring(0, 10) : "";
        if (local.note !== origNote || local.date !== origDate) {
            let newDate = null;
            if (local.date) {
                const origTime = n.date ? new Date(n.date).toISOString().substring(10) : "T00:00:00.000Z";
                newDate = local.date + origTime;
            }
            onUpdateNote(torkId, n.id, { ...n, note: local.note || null, date: newDate });
        }
    };

    const toggleHistory = (torkId) =>
        setHistoryOpen(prev => ({ ...prev, [torkId]: !prev[torkId] }));
    const toggleNoteForm = (torkId) =>
        setNoteFormOpen(prev => ({ ...prev, [torkId]: !prev[torkId] }));

    const openCreate = () => { setEditing(null); setName(""); setDescription(""); setShowForm(true); };
    const openEdit = (t) => { setEditing(t); setName(t.name); setDescription(t.description || ""); setShowForm(true); };

    const submitForm = (e) => {
        e.preventDefault();
        if (!name.trim()) return;
        if (editing) onUpdate({ ...editing, name: name.trim(), description });
        else onCreate({ name: name.trim(), description });
        setShowForm(false);
    };

    const submitNote = (torkId) => {
        const draft = getDraft(torkId);
        if (!draft.type) return;
        onAddNote(torkId, {
            note: draft.note.trim() || null,
            type: draft.type,
            date: draft.date || null,
            status: draft.status,
        });
        setNoteDrafts(prev => ({ ...prev, [torkId]: emptyDraft }));
        setNoteFormOpen(prev => ({ ...prev, [torkId]: false }));
    };

    const sorted = sortTorks(torks);
    const withNotesCount = sorted.filter(t => (t.notes || []).some(n => n.status !== "done")).length;
    const query = search.trim().toLowerCase();
    const visible = sorted
        .filter(t => !onlyWithNotes || (t.notes || []).some(n => n.status !== "done"))
        .filter(t => !query || (t.name || "").toLowerCase().includes(query));

    return (
        <>
        <div>
            {/* Toolbar: summary + filter + create */}
            <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
                <div className="flex flex-wrap items-center gap-3">
                    <SearchInput
                        value={search}
                        onChange={setSearch}
                        placeholder="Sök tork…"
                        className="w-56"
                    />
                    <span className="text-sm text-gray-500">
                        {sorted.length} torkar
                        {withNotesCount > 0 && (
                            <span className="ml-1.5 text-amber-600 font-medium">· {withNotesCount} med aktiva noteringar</span>
                        )}
                    </span>
                    {withNotesCount > 0 && (
                        <button
                            onClick={() => setOnlyWithNotes(v => !v)}
                            className={
                                "px-3 py-1.5 rounded-full text-xs border transition " +
                                (onlyWithNotes
                                    ? "bg-amber-100 border-amber-300 text-amber-800 font-medium"
                                    : "bg-white border-gray-300 text-gray-600 hover:bg-gray-100")
                            }
                        >
                            Endast med noteringar
                        </button>
                    )}
                </div>
                {!readOnly && (
                    <button onClick={openCreate}
                        className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700 text-sm font-medium shadow-sm">
                        + Ny tork
                    </button>
                )}
            </div>

            {sorted.length === 0 ? (
                <p className="text-gray-500">Inga torkar skapade.</p>
            ) : visible.length === 0 ? (
                <p className="text-gray-500">
                    {query ? "Inga torkar matchar sökningen." : "Inga torkar matchar filtret."}
                </p>
            ) : (
                <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
                    {visible.map(t => {
                        const allNotes = t.notes || [];
                        const activeNotes = allNotes.filter(n => n.status !== "done");
                        const doneNotes = allNotes.filter(n => n.status === "done");
                        const logs = t.torkLogs || [];
                        const draft = getDraft(t.id);
                        const isHistoryOpen = !!historyOpen[t.id];
                        const isNoteFormOpen = !!noteFormOpen[t.id];
                        const hasActive = activeNotes.length > 0;

                        return (
                            <div key={t.id}
                                className={"flex flex-col border rounded-xl shadow-sm overflow-hidden bg-white " + (hasActive ? "border-amber-300" : "border-gray-200")}>

                                {/* Card header: big name + status badge */}
                                <div className={"flex items-start justify-between gap-2 px-4 py-3 " + (hasActive ? "bg-amber-50" : "bg-gray-50")}>
                                    <div className="min-w-0">
                                        <div className="flex items-center gap-2">
                                            <span className="font-bold text-gray-900 text-lg leading-tight">{t.name}</span>
                                            {hasActive ? (
                                                <span className="px-2 py-0.5 rounded-full bg-amber-200 text-amber-900 text-xs font-semibold whitespace-nowrap">
                                                    {activeNotes.length} {activeNotes.length === 1 ? "notering" : "noteringar"}
                                                </span>
                                            ) : (
                                                <span className="px-2 py-0.5 rounded-full bg-green-100 text-green-700 text-xs font-semibold">OK</span>
                                            )}
                                        </div>
                                        {t.description && (
                                            <p className="text-xs text-gray-500 mt-0.5 truncate">{t.description}</p>
                                        )}
                                    </div>
                                    {!readOnly && (
                                        <div className="flex items-center gap-1 shrink-0">
                                            <button onClick={() => openEdit(t)} title="Redigera tork"
                                                className="p-1.5 rounded-md text-gray-400 hover:text-gray-700 hover:bg-white/70 transition">
                                                <PencilIcon />
                                            </button>
                                            <button onClick={() => setConfirmDeleteTorkId(t.id)} title="Ta bort tork"
                                                className="p-1.5 rounded-md text-gray-400 hover:text-red-600 hover:bg-white/70 transition">
                                                <TrashIcon />
                                            </button>
                                        </div>
                                    )}
                                </div>

                                {/* Active notes */}
                                <div className="flex-1 px-4 py-3">
                                    {activeNotes.length === 0 ? (
                                        <p className="text-sm text-gray-400">Inga aktiva noteringar</p>
                                    ) : (
                                        <ul className="space-y-2.5">
                                            {activeNotes.map(n => {
                                                const canEdit = !readOnly && (n.status === "planned" || n.status === "ongoing");
                                                const local = canEdit ? getNoteEdit(t.id, n.id, n) : null;
                                                return (
                                                    <li key={n.id} className="group rounded-lg border border-gray-100 bg-gray-50/60 px-3 py-2">
                                                        <div className="flex items-center gap-2">
                                                            <TypeDot noteTypes={noteTypes} type={n.type} />
                                                            <span className="font-medium text-sm text-gray-800 flex-1 min-w-0 truncate">{n.type}</span>
                                                            {readOnly ? (
                                                                <span className={"text-xs rounded px-2 py-1 " + noteStatusStyle(n.status)}>
                                                                    {noteStatusLabel(n.status)}
                                                                </span>
                                                            ) : (
                                                                <select
                                                                    className={"text-xs rounded px-2 py-1 " + noteStatusStyle(n.status)}
                                                                    value={n.status}
                                                                    onChange={e => onNoteStatus(t.id, n.id, e.target.value)}
                                                                >
                                                                    <option value="planned">Planerad</option>
                                                                    <option value="ongoing">Pågående</option>
                                                                    <option value="done">Klar</option>
                                                                </select>
                                                            )}
                                                            {!readOnly && (
                                                                <button
                                                                    onClick={() => setConfirmDeleteNoteKey(`${t.id}:${n.id}`)}
                                                                    title="Ta bort notering"
                                                                    className="text-gray-300 hover:text-red-500 transition opacity-0 group-hover:opacity-100"
                                                                >
                                                                    <TrashIcon />
                                                                </button>
                                                            )}
                                                        </div>
                                                        <div className="flex items-baseline gap-2 mt-1 pl-4 text-sm">
                                                            <span className="flex-1 min-w-0 text-gray-700 whitespace-normal break-words">
                                                                {canEdit ? (
                                                                    <InlineEdit
                                                                        value={local.note}
                                                                        placeholder="Lägg till text…"
                                                                        onChange={v => patchNoteEdit(t.id, n.id, { note: v })}
                                                                        onBlur={() => saveNoteIfChanged(t.id, n)}
                                                                    />
                                                                ) : (
                                                                    n.note || <span className="text-gray-400">–</span>
                                                                )}
                                                            </span>
                                                            <span className="text-gray-500 text-xs whitespace-nowrap">
                                                                {canEdit ? (
                                                                    <InlineDateEdit
                                                                        value={local.date}
                                                                        onChange={v => patchNoteEdit(t.id, n.id, { date: v })}
                                                                        onBlur={() => saveNoteIfChanged(t.id, n)}
                                                                    />
                                                                ) : (
                                                                    fmtDate(n.date)
                                                                )}
                                                            </span>
                                                        </div>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    )}
                                </div>

                                {/* Card footer: add note + history */}
                                <div className="px-4 pb-3">
                                    {!readOnly && !isNoteFormOpen && (
                                        <div className="flex items-center gap-2">
                                            <button
                                                onClick={() => toggleNoteForm(t.id)}
                                                className="flex-1 px-3 py-2 rounded-lg border border-dashed border-gray-300 text-sm text-gray-500 hover:border-blue-400 hover:text-blue-600 hover:bg-blue-50/50 transition"
                                            >
                                                + Ny notering
                                            </button>
                                            {(doneNotes.length > 0 || logs.length > 0) && (
                                                <HistoryButton isOpen={isHistoryOpen} count={doneNotes.length} onClick={() => toggleHistory(t.id)} />
                                            )}
                                        </div>
                                    )}
                                    {readOnly && (doneNotes.length > 0 || logs.length > 0) && (
                                        <HistoryButton isOpen={isHistoryOpen} count={doneNotes.length} onClick={() => toggleHistory(t.id)} />
                                    )}

                                    {/* Add note form — expanded on demand */}
                                    {!readOnly && isNoteFormOpen && (
                                        <div className="rounded-lg border border-blue-200 bg-blue-50/40 p-3 space-y-2">
                                            <div className="grid grid-cols-2 gap-2">
                                                <div>
                                                    <label className="block text-xs text-gray-500 mb-1">Typ *</label>
                                                    <select className="input !py-1.5 w-full" value={draft.type} autoFocus
                                                        onChange={e => patchDraft(t.id, { type: e.target.value })}>
                                                        <option value="">Välj typ</option>
                                                        {noteTypes.map(nt => <option key={nt.id} value={nt.name}>{nt.name}</option>)}
                                                    </select>
                                                </div>
                                                <div>
                                                    <label className="block text-xs text-gray-500 mb-1">Datum</label>
                                                    <input type="date" className="input !py-1.5 w-full" value={draft.date}
                                                        onChange={e => patchDraft(t.id, { date: e.target.value })} />
                                                </div>
                                            </div>
                                            <div>
                                                <label className="block text-xs text-gray-500 mb-1">Notering</label>
                                                <input
                                                    className="input !py-1.5 w-full"
                                                    placeholder="Skriv notering…"
                                                    value={draft.note}
                                                    onChange={e => patchDraft(t.id, { note: e.target.value })}
                                                    onKeyDown={e => { if (e.key === "Enter") submitNote(t.id); }}
                                                />
                                            </div>
                                            <div className="flex items-center gap-2">
                                                <select className="input !py-1.5 w-32" value={draft.status}
                                                    onChange={e => patchDraft(t.id, { status: e.target.value })}>
                                                    <option value="planned">Planerad</option>
                                                    <option value="ongoing">Pågående</option>
                                                </select>
                                                <div className="flex-1" />
                                                <button
                                                    onClick={() => toggleNoteForm(t.id)}
                                                    className="px-3 py-1.5 rounded-lg border border-gray-300 text-sm text-gray-600 hover:bg-gray-100"
                                                >
                                                    Avbryt
                                                </button>
                                                <button
                                                    onClick={() => submitNote(t.id)}
                                                    disabled={!draft.type}
                                                    className="px-4 py-1.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700 text-sm font-medium disabled:opacity-40 disabled:cursor-not-allowed"
                                                >
                                                    Spara
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* History section — expandable */}
                                {isHistoryOpen && (
                                    <div className="border-t bg-gray-50 px-4 py-3 space-y-4">
                                        <div>
                                            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Avklarade noteringar</h4>
                                            {doneNotes.length === 0 ? (
                                                <p className="text-sm text-gray-400">Inga avklarade noteringar.</p>
                                            ) : (
                                                <ul className="space-y-1.5">
                                                    {doneNotes.map(n => (
                                                        <li key={n.id} className="group flex items-start gap-2 text-sm opacity-70 hover:opacity-100 transition-opacity">
                                                            <TypeDot noteTypes={noteTypes} type={n.type} />
                                                            <span className="font-medium text-gray-700 whitespace-nowrap">{n.type}</span>
                                                            <span className="flex-1 min-w-0 text-gray-600 whitespace-normal break-words">
                                                                {n.note || <span className="text-gray-400">–</span>}
                                                            </span>
                                                            <span className="text-gray-400 text-xs whitespace-nowrap">{fmtDate(n.date)}</span>
                                                            {!readOnly && (
                                                                <>
                                                                <select
                                                                    className={"text-xs rounded px-1.5 py-0.5 " + noteStatusStyle(n.status)}
                                                                    value={n.status}
                                                                    onChange={e => onNoteStatus(t.id, n.id, e.target.value)}
                                                                >
                                                                    <option value="planned">Planerad</option>
                                                                    <option value="ongoing">Pågående</option>
                                                                    <option value="done">Klar</option>
                                                                </select>
                                                                <button
                                                                    onClick={() => setConfirmDeleteNoteKey(`${t.id}:${n.id}`)}
                                                                    title="Ta bort notering"
                                                                    className="text-gray-300 hover:text-red-500 transition opacity-0 group-hover:opacity-100"
                                                                >
                                                                    <TrashIcon />
                                                                </button>
                                                                </>
                                                            )}
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                        <div>
                                            <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Händelselogg</h4>
                                            {logs.length === 0 ? (
                                                <p className="text-sm text-gray-400">Ingen historik.</p>
                                            ) : (
                                                <ul className="text-sm text-gray-600 space-y-1">
                                                    {logs.slice().reverse().map((log, i) => (
                                                        <li key={i}>
                                                            <span className="text-gray-400">{fmt(log.timestamp)}</span>
                                                            {" · "}{log.action}
                                                            {log.actor && <span className="text-gray-400"> ({log.actor})</span>}
                                                        </li>
                                                    ))}
                                                </ul>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Create / edit modal */}
            {showForm && !readOnly && (
                <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4"
                    onClick={() => setShowForm(false)}>
                    <form onSubmit={submitForm}
                        className="bg-white rounded-xl shadow-xl w-full max-w-md p-6"
                        onClick={e => e.stopPropagation()}>
                        <h2 className="text-xl font-semibold mb-4">{editing ? "Redigera tork" : "Ny tork"}</h2>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Namn *</label>
                        <input className="input mb-4" value={name} onChange={e => setName(e.target.value)} required autoFocus />
                        <label className="block text-sm font-medium text-gray-700 mb-1">Beskrivning</label>
                        <textarea className="input mb-5" rows={2} value={description}
                            onChange={e => setDescription(e.target.value)} />
                        <div className="flex justify-end gap-2">
                            <button type="button" onClick={() => setShowForm(false)}
                                className="px-4 py-2 rounded-lg border border-gray-300 hover:bg-gray-100">Avbryt</button>
                            <button type="submit"
                                className="px-4 py-2 rounded-lg bg-blue-600 text-white hover:bg-blue-700">Spara</button>
                        </div>
                    </form>
                </div>
            )}
        </div>

        {confirmDeleteTorkId && (
            <ApproveDenyModal
                onAccept={() => { onDelete(confirmDeleteTorkId); setConfirmDeleteTorkId(null); }}
                onDeny={() => setConfirmDeleteTorkId(null)}
            >
                Vill du verkligen ta bort torken?
            </ApproveDenyModal>
        )}
        {confirmDeleteNoteKey && (() => {
            const [torkId, noteId] = confirmDeleteNoteKey.split(":");
            return (
                <ApproveDenyModal
                    onAccept={() => { onDeleteNote(torkId, noteId); setConfirmDeleteNoteKey(null); }}
                    onDeny={() => setConfirmDeleteNoteKey(null)}
                >
                    Vill du verkligen ta bort noteringen?
                </ApproveDenyModal>
            );
        })()}
        </>
    );
};

const HistoryButton = ({ isOpen, count, onClick }) => (
    <button
        onClick={onClick}
        className={
            "px-3 py-2 rounded-lg border text-sm transition whitespace-nowrap " +
            (isOpen
                ? "border-gray-400 bg-gray-100 text-gray-700"
                : "border-gray-300 bg-white hover:bg-gray-100 text-gray-600")
        }
    >
        Historik{count > 0 ? ` (${count})` : ""}
    </button>
);

const TrashIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7h6m2 0a1 1 0 00-1-1h-4a1 1 0 00-1 1m-4 0h10" />
    </svg>
);

const PencilIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L6.832 19.82a4.5 4.5 0 01-1.897 1.13l-2.685.8.8-2.685a4.5 4.5 0 011.13-1.897L16.862 4.487z" />
    </svg>
);

export const TypeDot = ({ noteTypes, type }) => {
    const color = noteTypes.find(nt => nt.name === type)?.color || "#9CA3AF";
    return <span className="inline-block w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ backgroundColor: color }} />;
};

const InlineEdit = ({ value, placeholder = "–", onChange, onBlur }) => {
    const [editing, setEditing] = useState(false);
    const inputRef = useRef(null);

    const activate = () => { setEditing(true); setTimeout(() => inputRef.current?.focus(), 0); };
    const handleBlur = () => { setEditing(false); onBlur?.(); };

    if (editing) {
        return (
            <input
                ref={inputRef}
                className="input !py-0.5 w-full min-w-[120px]"
                value={value}
                onChange={e => onChange(e.target.value)}
                onBlur={handleBlur}
            />
        );
    }
    return (
        <span
            onClick={activate}
            title="Klicka för att redigera"
            className="cursor-text rounded px-1 -mx-1 hover:bg-gray-100 transition-colors"
        >
            {value || <span className="text-gray-400">{placeholder}</span>}
        </span>
    );
};

const InlineDateEdit = ({ value, onChange, onBlur }) => {
    const [editing, setEditing] = useState(false);
    const inputRef = useRef(null);

    const activate = () => { setEditing(true); setTimeout(() => inputRef.current?.showPicker?.() ?? inputRef.current?.focus(), 0); };
    const handleBlur = () => { setEditing(false); onBlur?.(); };

    if (editing) {
        return (
            <input
                ref={inputRef}
                type="date"
                className="input !py-0.5 w-32"
                value={value}
                onChange={e => onChange(e.target.value)}
                onBlur={handleBlur}
            />
        );
    }
    return (
        <span
            onClick={activate}
            title="Klicka för att redigera"
            className="cursor-text rounded px-1 -mx-1 hover:bg-gray-100 transition-colors whitespace-nowrap"
        >
            {value ? fmtDate(value) : <span className="text-gray-400">–</span>}
        </span>
    );
};

export default TorksManager;
